import { Form, Select, Typography } from 'antd';
import type { ExistingUserRolePolicy, ExistingUserLocationPolicy } from './types';
import { USER_TYPE_OPTIONS } from './userTypes';

/**
 * SF-3180 Task 3 / SF-3593 — selectors for `existingUserRolePolicy`,
 * `existingUserLocationPolicy`, and `defaultType`.
 *
 * These three fields control how sf-auth-service handles post-login
 * resolution of user attributes (see SF-3404 `src/routes/ssoConfig.ts`,
 * SF-3591 location policy, and `src/utils/roleMapping.ts`):
 *
 *  - `existingUserRolePolicy` — on each SSO login, when the user already
 *    exists in sf-api, decides whether to `preserve` the stored `user.type`,
 *    `overwrite` it with the rule-matched type, or `flag` the user for review
 *    when the two disagree. Default: `preserve` (safest — non-destructive).
 *  - `existingUserLocationPolicy` — same pattern, but for the user's
 *    locations: `preserve` keeps them as managed in ServeFirst, `overwrite`
 *    replaces them with the IdP-provided list on every login. Default:
 *    `preserve` (non-destructive — admins typically own location assignment
 *    in ServeFirst even when SSO is enabled).
 *  - `defaultType` — the fallback `user.type` applied when no `roleToType`
 *    rule matches (and the user is new). Must be a USER_TYPES enum value.
 *    Default: `employee` — the narrowest-permission internal type, so an
 *    unmatched login can't accidentally be provisioned with admin rights.
 *
 * Pure controlled component: Tab owns state, diffs against the loaded
 * config to build the PATCH payload via `buildPatchPayload`.
 */
export interface PolicyAndDefaultSelectorsProps {
  policy: ExistingUserRolePolicy;
  locationPolicy: ExistingUserLocationPolicy;
  defaultType: string;
  onPolicyChange: (p: ExistingUserRolePolicy) => void;
  onLocationPolicyChange: (p: ExistingUserLocationPolicy) => void;
  onDefaultTypeChange: (t: string) => void;
  disabled?: boolean;
}

const POLICY_OPTIONS: { value: ExistingUserRolePolicy; label: string }[] = [
  { value: 'preserve', label: 'Preserve existing role' },
  { value: 'overwrite', label: 'Overwrite with matched role' },
  { value: 'flag', label: 'Flag for review on mismatch' },
];

/**
 * SF-3593 — `existingUserLocationPolicy` only has two values; there is no
 * `flag` mode for locations because the auth-service treats location
 * disagreement as non-fatal (locations are typically managed in
 * ServeFirst, not the IdP).
 */
const LOCATION_POLICY_OPTIONS: {
  value: ExistingUserLocationPolicy;
  label: string;
}[] = [
  { value: 'preserve', label: 'Preserve existing locations' },
  { value: 'overwrite', label: 'Overwrite with IdP locations' },
];

export const PolicyAndDefaultSelectors: React.FC<PolicyAndDefaultSelectorsProps> = ({
  policy,
  locationPolicy,
  defaultType,
  onPolicyChange,
  onLocationPolicyChange,
  onDefaultTypeChange,
  disabled,
}) => {
  return (
    <div>
      <Form.Item
        label="Existing user role policy"
        extra="What to do on SSO login when the user already exists in ServeFirst and their stored user type differs from the rule-matched type."
      >
        <Select
          value={policy}
          onChange={onPolicyChange}
          options={POLICY_OPTIONS}
          disabled={disabled}
          aria-label="Existing user role policy"
        />
      </Form.Item>
      <Form.Item
        label="Existing user location policy"
        extra="What to do on SSO login with the user's locations: preserve keeps existing locations (managed in ServeFirst), overwrite replaces them with the IdP-provided list on every login (SSO is the source of truth)."
      >
        <Select
          value={locationPolicy}
          onChange={onLocationPolicyChange}
          options={LOCATION_POLICY_OPTIONS}
          disabled={disabled}
          aria-label="Existing user location policy"
        />
      </Form.Item>
      <Form.Item
        label="Default user type"
        extra="Fallback user type applied when no role-mapping rule matches (only applied to NEW users; existing users are governed by the policy above)."
      >
        <Select
          value={defaultType}
          onChange={onDefaultTypeChange}
          options={USER_TYPE_OPTIONS}
          disabled={disabled}
          aria-label="Default user type"
        />
      </Form.Item>
      <Typography.Paragraph type="secondary" style={{ marginTop: -4 }}>
        All three fields are enforced by sf-auth-service — invalid values are rejected at save time
        with a validation error.
      </Typography.Paragraph>
    </div>
  );
};
